import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Avatar,
    LinearProgress,
    Stack,
    FormControl,
    Select,
    MenuItem,
    InputLabel
} from '@mui/material';
import { motion } from 'framer-motion';

// Role hierarchy: each role maps to the subordinate roles below it
const ROLE_HIERARCHY = {
    national_head: [
        { value: 'zone_head', label: 'Zone Head' },
        { value: 'state_head', label: 'State Head' },
        { value: 'middle_manager', label: 'Team Leader' },
        { value: 'manager', label: 'Frontline' }
    ],
    zone_head: [
        { value: 'state_head', label: 'State Head' },
        { value: 'middle_manager', label: 'Team Leader' },
        { value: 'manager', label: 'Frontline' }
    ],
    state_head: [
        { value: 'middle_manager', label: 'Team Leader' },
        { value: 'manager', label: 'Frontline' }
    ],
    team_leader: [{ value: 'manager', label: 'Frontline' }],
    frontline: []
};

// Role abbreviations and colors for the avatar
const ROLE_META = {
    national_head: { abbr: 'NH', color: '#7c3aed', bg: '#ede9fe' },
    zone_head: { abbr: 'ZH', color: '#0369a1', bg: '#e0f2fe' },
    state_head: { abbr: 'SH', color: '#0f766e', bg: '#ccfbf1' },
    middle_manager: { abbr: 'TL', color: '#b45309', bg: '#fef3c7' },
    manager: { abbr: 'FL', color: '#be123c', bg: '#ffe4e6' }
};

const getRoleMeta = (role) => ROLE_META[role] || { abbr: role?.slice(0, 2).toUpperCase() || '?', color: '#475569', bg: '#f1f5f9' };

const TeamPerformanceTable = ({ teamData, userRole, selectedRole, onRoleChange }) => {
    // Extract dynamic performance keys from the first member or performance data
    const performanceKeys = teamData?.[0]?.performance ? Object.keys(teamData[0].performance) : [];

    // Get subordinate role options for the logged-in user's role
    const roleOptions = ROLE_HIERARCHY[userRole] || [];

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
        >
            <Paper
                elevation={0}
                sx={{
                    p: 4,
                    borderRadius: '24px',
                    background: '#fff',
                    border: '1px solid',
                    borderColor: 'grey.100',
                    boxShadow: '0 10px 40px -10px rgba(0,0,0,0.05)'
                }}
            >
                {/* Header row with title and role filter dropdown */}
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    justifyContent="space-between"
                    spacing={2}
                    sx={{ mb: 4 }}
                >
                    <Typography variant="h5" fontWeight={800}>
                        Team Performance Comparison
                    </Typography>

                    {roleOptions.length > 0 && (
                        <FormControl size="small" sx={{ minWidth: 170 }}>
                            <InputLabel id="role-filter-label" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                                Filter by Role
                            </InputLabel>
                            <Select
                                labelId="role-filter-label"
                                value={selectedRole || ''}
                                label="Filter by Role"
                                onChange={(e) => onRoleChange(e.target.value || null)}
                                sx={{
                                    borderRadius: '12px',
                                    fontWeight: 700,
                                    fontSize: '0.85rem',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'grey.200'
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'primary.main'
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'primary.main'
                                    }
                                }}
                            >
                                <MenuItem value="">
                                    <em>All Roles</em>
                                </MenuItem>
                                {roleOptions.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value} sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </Stack>

                <TableContainer>
                    <Table sx={{ minWidth: 800 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 800, color: 'text.secondary', bgcolor: 'grey.50/50' }}>MEMBER</TableCell>
                                {performanceKeys.map((key) => (
                                    <TableCell
                                        key={key}
                                        sx={{ fontWeight: 800, color: 'text.secondary', bgcolor: 'grey.50/50', textTransform: 'uppercase' }}
                                    >
                                        {teamData[0].performance[key].metric}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {teamData.map((member, index) => (
                                <TableRow
                                    key={member.id}
                                    component={motion.tr}
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.3, delay: index * 0.05 }}
                                    hover
                                    sx={{
                                        opacity: member.status === 'Deactive' ? 0.75 : 1,
                                        '&:hover': {
                                            bgcolor: member.status === 'Deactive' ? 'transparent' : 'primary.50/30 !important',
                                            '& td': { color: member.status === 'Deactive' ? 'inherit' : 'primary.main' }
                                        },
                                        pointerEvents: member.status === 'Deactive' ? 'none' : 'auto',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    <TableCell sx={{ py: 1.5 }}>
                                        {(() => {
                                            const meta = getRoleMeta(member.role);
                                            const headMeta = member.head_role ? getRoleMeta(member.head_role) : null;
                                            const isInactive = member.status === 'Deactive';
                                            return (
                                                <Stack direction="row" spacing={1.5} alignItems="center">
                                                    {/* Avatar with role abbreviation + inactive dot */}
                                                    <Box sx={{ position: 'relative', flexShrink: 0 }}>
                                                        <Avatar
                                                            sx={{
                                                                bgcolor: meta.bg,
                                                                color: meta.color,
                                                                fontWeight: 900,
                                                                fontSize: '0.7rem',
                                                                letterSpacing: 0.5,
                                                                width: 42,
                                                                height: 42,
                                                                border: `2px solid ${meta.color}22`,
                                                                filter: isInactive ? 'grayscale(0.6)' : 'none',
                                                                transition: 'all 0.3s ease'
                                                            }}
                                                        >
                                                            {meta.abbr}
                                                        </Avatar>
                                                        {isInactive && (
                                                            <Box
                                                                sx={{
                                                                    position: 'absolute',
                                                                    bottom: 1,
                                                                    right: 1,
                                                                    width: 10,
                                                                    height: 10,
                                                                    bgcolor: '#ef4444',
                                                                    border: '1.5px solid #fff',
                                                                    borderRadius: '50%'
                                                                }}
                                                            />
                                                        )}
                                                    </Box>

                                                    {/* Name + compact subtitle */}
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography
                                                            variant="subtitle2"
                                                            fontWeight={800}
                                                            noWrap
                                                            sx={{ lineHeight: 1.3, color: isInactive ? 'text.disabled' : 'text.primary' }}
                                                        >
                                                            {member.name}
                                                        </Typography>
                                                        <Stack
                                                            direction="row"
                                                            spacing={0.5}
                                                            alignItems="center"
                                                            flexWrap="wrap"
                                                            sx={{ mt: 0.3 }}
                                                        >
                                                            {/* Role badge pill */}
                                                            <Box
                                                                sx={{
                                                                    px: 0.8,
                                                                    py: 0.1,
                                                                    borderRadius: '6px',
                                                                    bgcolor: meta.bg,
                                                                    color: meta.color,
                                                                    fontSize: '0.6rem',
                                                                    fontWeight: 900,
                                                                    letterSpacing: 0.5,
                                                                    lineHeight: 1.6
                                                                }}
                                                            >
                                                                {meta.abbr}
                                                            </Box>

                                                            {/* Separator dot */}
                                                            {member.head_name && (
                                                                <Box
                                                                    sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'grey.300' }}
                                                                />
                                                            )}

                                                            {/* Under info */}
                                                            {member.head_name && (
                                                                <Typography
                                                                    variant="caption"
                                                                    noWrap
                                                                    sx={{ fontSize: '0.67rem', color: 'text.disabled', fontWeight: 600 }}
                                                                >
                                                                    {member.head_name}
                                                                    {headMeta && (
                                                                        <Box
                                                                            component="span"
                                                                            sx={{
                                                                                ml: 0.4,
                                                                                px: 0.5,
                                                                                py: 0.05,
                                                                                borderRadius: '4px',
                                                                                bgcolor: headMeta.bg,
                                                                                color: headMeta.color,
                                                                                fontSize: '0.6rem',
                                                                                fontWeight: 900
                                                                            }}
                                                                        >
                                                                            {headMeta.abbr}
                                                                        </Box>
                                                                    )}
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                    </Box>
                                                </Stack>
                                            );
                                        })()}
                                    </TableCell>

                                    {performanceKeys.map((key) => {
                                        const metrics = member.performance?.[key];
                                        if (!metrics) return <TableCell key={key}>-</TableCell>;
                                        return (
                                            <TableCell key={key}>
                                                <Stack spacing={1}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Typography variant="body2" fontWeight={800} sx={{ fontSize: '0.85rem' }}>
                                                            {metrics.achieved.toLocaleString()}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" fontWeight={800}>
                                                            {Math.round(metrics.percentage)}%
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ width: '100%' }}>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={metrics.percentage > 100 ? 100 : metrics.percentage}
                                                            sx={{
                                                                height: 6,
                                                                borderRadius: 3,
                                                                bgcolor: 'grey.100',
                                                                '& .MuiLinearProgress-bar': {
                                                                    borderRadius: 3,
                                                                    bgcolor:
                                                                        metrics.status.toLowerCase() === 'green'
                                                                            ? '#22c55e'
                                                                            : metrics.status.toLowerCase() === 'yellow'
                                                                            ? '#f59e0b'
                                                                            : '#ef4444'
                                                                }
                                                            }}
                                                        />
                                                    </Box>
                                                </Stack>
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </motion.div>
    );
};

TeamPerformanceTable.propTypes = {
    teamData: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string,
            role: PropTypes.string,
            status: PropTypes.string,
            performance: PropTypes.object
        })
    ).isRequired,
    userRole: PropTypes.string,
    selectedRole: PropTypes.string,
    onRoleChange: PropTypes.func
};

export default TeamPerformanceTable;
